// Competition service for managing tournaments
import { Competition, ApiResponse } from '../types'

type CompetitionResponse = ApiResponse<Competition | Competition[]>

class CompetitionService {
  async getActiveCompetitions(): Promise<Competition[]> {
    // Simulate API call
    return new Promise((resolve) => {
      setTimeout(() => {
        const now = new Date()
        // Generate dates relative to current date for realistic mock data
        const addDays = (days: number) => {
          const date = new Date()
          date.setDate(date.getDate() + days)
          return date.toISOString().split('T')[0]
        }

        const competitions: Competition[] = [
          {
            id: '1',
            name: 'Summer Trading Championship',
            participants: 234,
            startDate: addDays(-10), // Started 10 days ago
            endDate: addDays(20), // Ends in 20 days
            logo: 'https://via.placeholder.com/40',
            prizes: '3',
            mode: 'live',
            status: 'ends_in' as any // Active competition
          },
          {
            id: '2',
            name: 'Beginner\'s League Q2',
            participants: 156,
            startDate: addDays(-5), // Started 5 days ago
            endDate: addDays(15), // Ends in 15 days
            logo: 'https://via.placeholder.com/40',
            prizes: '5',
            mode: 'demo',
            status: 'ends_in' as any // Active competition
          },
          {
            id: '3',
            name: 'Pro Trader Challenge',
            participants: 89,
            startDate: addDays(7), // Starts in 7 days
            endDate: addDays(37), // Ends in 37 days
            logo: 'https://via.placeholder.com/40',
            prizes: '3',
            mode: 'live',
            status: 'begins_in' as any // Upcoming competition
          },
          {
            id: '4',
            name: 'Weekly Sprint #24',
            participants: 342,
            startDate: addDays(-3), // Started 3 days ago
            endDate: addDays(4), // Ends in 4 days
            logo: 'https://via.placeholder.com/40',
            prizes: '4',
            mode: 'live',
            status: 'ends_in' as any // Active competition
          },
          {
            id: '5',
            name: 'Risk Management Masters',
            participants: 127,
            startDate: addDays(-1), // Started yesterday
            endDate: addDays(13), // Ends in 13 days
            logo: 'https://via.placeholder.com/40',
            prizes: '5',
            mode: 'demo',
            status: 'ends_in' as any // Active competition
          },
          {
            id: '6',
            name: 'Forex Masters Cup',
            participants: 298,
            startDate: addDays(-7), // Started 7 days ago
            endDate: addDays(10), // Ends in 10 days
            logo: 'https://via.placeholder.com/40',
            prizes: '4',
            mode: 'live',
            status: 'ends_in' as any // Active competition
          },
          {
            id: '7',
            name: 'Scalping Tournament',
            participants: 175,
            startDate: addDays(-2), // Started 2 days ago
            endDate: addDays(5), // Ends in 5 days
            logo: 'https://via.placeholder.com/40',
            prizes: '3',
            mode: 'live',
            status: 'ends_in' as any // Active competition
          },
          {
            id: '8',
            name: 'Crypto Trading Battle',
            participants: 412,
            startDate: addDays(-12), // Started 12 days ago
            endDate: addDays(18), // Ends in 18 days
            logo: 'https://via.placeholder.com/40',
            prizes: '5',
            mode: 'live',
            status: 'ends_in' as any // Active competition
          },
          {
            id: '9',
            name: 'Index Futures Championship',
            participants: 267,
            startDate: addDays(3), // Starts in 3 days
            endDate: addDays(33), // Ends in 33 days
            logo: 'https://via.placeholder.com/40',
            prizes: '4',
            mode: 'live',
            status: 'begins_in' as any // Upcoming competition
          },
          {
            id: '10',
            name: 'Demo Practice Arena',
            participants: 93,
            startDate: addDays(-8), // Started 8 days ago
            endDate: addDays(7), // Ends in 7 days
            logo: 'https://via.placeholder.com/40',
            prizes: '3',
            mode: 'demo',
            status: 'ends_in' as any // Active competition
          }
        ]

        resolve(competitions)
      }, 800)
    })
  }

  async getHistoricalCompetitions(period: string): Promise<Competition[]> {
    // Simulate API call
    return new Promise((resolve) => {
      setTimeout(() => {
        const historicalCompetitions: Competition[] = [
          {
            id: 'h1',
            name: 'Spring Championship 2024',
            participants: 412,
            endDate: '2024-03-31',
            prizes: '3',
            mode: 'live',
            status: 'completed'
          },
          {
            id: 'h2',
            name: 'New Year Challenge',
            participants: 289,
            endDate: '2024-01-31',
            prizes: '5',
            mode: 'live',
            status: 'completed'
          },
          {
            id: 'h3',
            name: 'Winter Trading Cup',
            participants: 167,
            endDate: '2023-12-31',
            prizes: '4',
            mode: 'demo',
            status: 'completed'
          }
        ]

        // Filter based on period
        if (period === 'Last Quarter') {
          resolve(historicalCompetitions.filter(c => {
            const endDate = new Date(c.endDate)
            const threeMonthsAgo = new Date()
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
            return endDate >= threeMonthsAgo
          }))
        } else if (period === 'Last 6 Months') {
          resolve(historicalCompetitions)
        } else {
          resolve(historicalCompetitions)
        }
      }, 600)
    })
  }

  async getCompetitionDetails(competitionId: string): Promise<Competition | null> {
    // Simulate API call
    return new Promise((resolve) => {
      setTimeout(() => {
        const addDays = (days: number) => {
          const date = new Date()
          date.setDate(date.getDate() + days)
          return date.toISOString().split('T')[0]
        }

        // Mock detailed competition data
        const competition: Competition = {
          id: competitionId,
          name: 'Summer Trading Championship',
          participants: 234,
          endDate: addDays(20), // Ends in 20 days
          startDate: addDays(-10), // Started 10 days ago
          registrationDeadline: addDays(-11), // Registration closed 11 days ago
          logo: 'https://via.placeholder.com/100',
          prizes: '3',
          mode: 'live',
          status: 'active',
          description: 'Join the most exciting trading competition of the summer. Trade your way to the top and win amazing prizes.'
        }

        resolve(competition)
      }, 500)
    })
  }

  async createCompetition(competitionData: Partial<Competition>): Promise<CompetitionResponse> {
    // Simulate API call
    return new Promise((resolve) => {
      setTimeout(() => {
        const newCompetition: Competition = {
          id: 'new-' + Date.now(),
          name: competitionData.name || 'New Competition',
          participants: 0,
          endDate: competitionData.endDate || '',
          startDate: competitionData.startDate || '',
          prizes: competitionData.prizes || '',
          mode: competitionData.mode || 'demo',
          status: 'upcoming',
          ...competitionData
        }

        resolve({
          success: true,
          data: newCompetition
        })
      }, 1000)
    })
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
    return date.toLocaleDateString('en-US', options)
  }

  getDaysRemaining(endDate: string): number {
    const end = new Date(endDate)
    const now = new Date()
    const diff = end.getTime() - now.getTime()
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }

  getCompetitionStatus(endDate: string): string {
    const daysRemaining = this.getDaysRemaining(endDate)
    if (daysRemaining < 0) {
      return 'Completed'
    } else if (daysRemaining === 0) {
      return 'Ending Today'
    } else if (daysRemaining <= 7) {
      return `${daysRemaining} days left`
    } else {
      return `Ends ${this.formatDate(endDate)}`
    }
  }

  calculateCountdown(targetDate: Date): { days: number; hours: number; minutes: number; seconds: number } {
    const now = new Date()
    const target = new Date(targetDate)
    const difference = target.getTime() - now.getTime()
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    return { days, hours, minutes, seconds }
  }
}

export default new CompetitionService()